import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Filter, ExternalLink, Info, Search, Boxes } from "lucide-react";

const ALL = "all";

export default function PortalVendorCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(ALL);
  const [showReason, setShowReason] = useState(null); // service id

  useEffect(() => {
    api
      .get("/clinic/vendor-services")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const s = new Set();
    items.forEach((it) => it.category && s.add(it.category));
    return [ALL, ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (category !== ALL && (it.category || "") !== category) return false;
      if (!search) return true;
      const hay = `${it.name} ${it.description || ""} ${it.vendor_name || ""}`.toLowerCase();
      return hay.includes(search.toLowerCase());
    });
  }, [items, category, search]);

  return (
    <div className="space-y-5" data-testid="portal-vendor-catalog">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600">
          Operations · Vendor service catalog
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1">Browse vendor-published services</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Approved services across the Nodera vendor network. Most are ordered directly inside the
          platform; occasionally a service genuinely requires a different ordering channel
          (e.g. compounded medications) — those are flagged with an info icon explaining why.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="portal-vendor-catalog-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services or vendors…"
            className="pl-9 pr-3 py-2 text-sm rounded-full border border-slate-200 bg-white w-64 focus:outline-none focus:border-slate-400"
          />
        </div>
        <Filter className="w-4 h-4 text-slate-400 ml-2" />
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`portal-vendor-catalog-cat-${c}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
              category === c
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {c === ALL ? "All" : c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Boxes className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-600">No vendor services match this filter yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((row) => (
            <article
              key={row.id}
              className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col"
              data-testid={`portal-vendor-catalog-card-${row.id}`}
            >
              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                {row.vendor_name} {row.category && `· ${row.category}`}
              </div>
              <h3 className="font-semibold text-slate-900 mt-0.5">{row.name}</h3>
              {row.description && (
                <p className="text-sm text-slate-600 mt-2 line-clamp-3">{row.description}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                {row.price != null && (
                  <span className="font-semibold text-slate-900">
                    ${Number(row.price).toFixed(2)}
                    <span className="text-slate-500 font-normal ml-1">
                      {(row.price_unit || "").replace("_", " ")}
                    </span>
                  </span>
                )}
                {row.sla_days != null && <span>SLA: {row.sla_days}d</span>}
              </div>
              <div className="mt-auto pt-4 flex items-center gap-2">
                {row.custom_link ? (
                  <>
                    <a
                      href={row.custom_link}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`portal-vendor-catalog-external-${row.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg px-3 py-1.5"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Order externally
                    </a>
                    <button
                      onClick={() =>
                        setShowReason((prev) => (prev === row.id ? null : row.id))
                      }
                      data-testid={`portal-vendor-catalog-why-${row.id}`}
                      title="Why is this service ordered off-platform?"
                      className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-2.5 py-1.5"
                    >
                      <Info className="w-3.5 h-3.5" /> Why off-platform?
                    </button>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                    Ordered inside Nodera
                  </span>
                )}
              </div>
              {showReason === row.id && (
                <div
                  className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900"
                  data-testid={`portal-vendor-catalog-reason-${row.id}`}
                >
                  <div className="font-bold uppercase tracking-wide text-[10px] text-amber-700 mb-1">
                    Vendor's justification
                  </div>
                  {row.custom_link_reason ||
                    "This vendor flagged this service as requiring a different ordering channel."}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
